import React, { Suspense, useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import type { FC, ReactElement, Dispatch, RefObject } from "react";

import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Text,
  useTheme,
  useToast,
  useDisclose,
  Center,
  Spinner,
} from "native-base";
import { useWindowDimensions } from "react-native";
import {
  useQueryLoader,
  usePreloadedQuery,
  usePaginationFragment,
  useRelayEnvironment,
  useMutation,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";
import { RecyclerListView, DataProvider, LayoutProvider } from "recyclerlistview";
import { fetchQuery } from "relay-runtime";
import type { Subscription } from "relay-runtime";

import RegisterMutationProgressIndicators from "pianofunclub-crm/components/Buttons/RegisterMutationProgressIndicators";
import type { RegisterMutationProgressIndicatorsRef } from "pianofunclub-crm/components/Buttons/RegisterMutationProgressIndicators";
import RegisterHubTableRow from "pianofunclub-crm/components/ListItems/RegisterHubTableRow";
import type {
  RegistersStackNavigatorProps,
  RegistersStackRouteProps,
} from "pianofunclub-crm/navigation/RegistersNavigator";

import LoadingBlobs from "pianofunclub-shared/components/Animations/LoadingBlobs";
import Row from "pianofunclub-shared/components/Base/Row";
import ButtonDebounced from "pianofunclub-shared/components/Buttons/ButtonDebounced";
import ListEmptyBanner from "pianofunclub-shared/components/ListItems/ListEmptyBanner";
import SchoolMultiSelectModal from "pianofunclub-shared/components/Modals/SchoolMultiSelectModal";
import SendRegisterChangeNotificationModal from "pianofunclub-shared/components/Modals/SendRegisterChangeNotificationModal";
import TeacherMultiSelectModal from "pianofunclub-shared/components/Modals/TeacherMultiSelectModal";
import Actionsheet from "pianofunclub-shared/components/NativeBaseExtended/Actionsheet";
import AlertPopup from "pianofunclub-shared/components/NativeBaseExtended/AlertPopup";
import Select from "pianofunclub-shared/components/NativeBaseExtended/Select";
import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import CustomFlatListSpinner from "pianofunclub-shared/components/Other/CustomFlatListSpinner";
import {
  AccountsIcon,
  CopyCalendarIcon,
  RegisterIcon,
  RestartIcon,
  SchoolIcon,
  SendIcon,
  SortDescDate,
} from "pianofunclub-shared/components/Other/Icons";
import SearchBar from "pianofunclub-shared/components/Other/SearchBar";
import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import type { LoadMutationProgressQuery } from "pianofunclub-shared/relay/graphql/general/__generated__/LoadMutationProgressQuery.graphql";
import { load_mutation_progress } from "pianofunclub-shared/relay/graphql/general/LoadMutationProgress";
import type {
  GenerateOrUpdateGoogleRegistersMutation,
  GenerateOrUpdateGoogleRegistersMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/GenerateOrUpdateGoogleRegistersMutation.graphql";
import type {
  GenerateRegistersForNewBlockMutation,
  GenerateRegistersForNewBlockMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/GenerateRegistersForNewBlockMutation.graphql";
import type {
  LoadRegisters_query_profiles$key,
  LoadRegisters_query_profiles$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/LoadRegisters_query_profiles.graphql";
import type { LoadRegistersPaginationQuery } from "pianofunclub-shared/relay/graphql/registers/__generated__/LoadRegistersPaginationQuery.graphql";
import type { LoadRegistersQuery } from "pianofunclub-shared/relay/graphql/registers/__generated__/LoadRegistersQuery.graphql";
import type {
  ReadInGoogleRegistersMutation,
  ReadInGoogleRegistersMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/ReadInGoogleRegistersMutation.graphql";
import { generate_or_update_google_registers } from "pianofunclub-shared/relay/graphql/registers/GenerateOrUpdateGoogleRegisters";
import { generate_registers_for_new_block } from "pianofunclub-shared/relay/graphql/registers/GenerateRegistersForNewBlock";
import {
  load_registers,
  load_registers_pagination,
} from "pianofunclub-shared/relay/graphql/registers/LoadRegisters";
import { read_in_google_registers } from "pianofunclub-shared/relay/graphql/registers/ReadInGoogleRegisters";
import { BLOCKS } from "pianofunclub-shared/utils/constants";
import { getScaledWindowDimension } from "pianofunclub-shared/utils/converters";
import {
  getCurrentBlock,
  getFullName,
  getDefaultStartingYearAndStartingYearsForSelect,
  getTermInWords,
  getPreviousBlockAndStartingYear,
} from "pianofunclub-shared/utils/extractors";
import { useDebounceFunction } from "pianofunclub-shared/utils/hooks";
import { createReducer } from "pianofunclub-shared/utils/reducers";
import type { State, Action } from "pianofunclub-shared/utils/reducers";

type NavigationProps = RegistersStackNavigatorProps<"RegistersHub">;

type RouteProps = RegistersStackRouteProps<"RegistersHub">;

interface ScreenProps {
  navigation: NavigationProps;
  route: RouteProps;
}

interface ContentProps {
  dispatchState: Dispatch<Action<ReducerValues>>;
  generateRegistersForNewBlockInFlight: boolean;
  navigation: NavigationProps;
  progressIndicatorRef: RefObject<RegisterMutationProgressIndicatorsRef | null>;
  queryReference: PreloadedQuery<LoadRegistersQuery, Record<string, unknown>>;
  state: State<ReducerValues>;
}

export type ShowMutationProgressIndicators = {
  generateGoogleRegisters: boolean;
  generateRegistersForNewBlock: boolean;
  readInRegisters: boolean;
  sendTeacherBulkCommunication: boolean;
};

export type ReducerValues = {
  block: number;
  contentIsRendered: boolean;
  dataProvider: DataProvider;
  failedMutationItems: string[];
  generateNewRegistersAlertIsOpen: boolean;
  generateNewRegistersForTeachersAlertIsOpen: boolean;
  generateNewRegistersForTeachersTeacherSelectModalOpen: boolean;
  generateSchoolGoogleRegistersModalIsOpen: boolean;
  generateTeacherGoogleRegistersModalIsOpen: boolean;
  isRefetching: boolean;
  layoutProvider?: LayoutProvider;
  readInRegistersModalIsOpen: boolean;
  refreshHandler?: () => void;
  searchTerm: string;
  sendRegisterChangeNotificationModalIsOpen?: boolean;
  sendTeacherBulkCommunicationInProgress?: boolean;
  showMutationProgressIndicators: ShowMutationProgressIndicators;
  startingYear: number;
  subscription?: Subscription;
  teacherIds?: string[];
};

export type TableData = NonNullable<LoadRegisters_query_profiles$data["profiles"]>["edges"][0];

const LOAD_X_SCHOOLS = 100;
const TABLE_BORDER_COLOR = "surface.400";
const TABLE_BORDER_WIDTH = 1;
const TABLE_BORDER_RADIUS = 10;
const FLEX_ARRAY = [1, 5, 5];
const REGISTERS_TO_GENERATE_IN_BATCH = 1;
const REGISTERS_TO_READ_IN_BATCH = 1;

const RegistersHubContent: FC<ContentProps> = (props) => {
  const {
    dispatchState,
    generateRegistersForNewBlockInFlight,
    navigation,
    progressIndicatorRef,
    queryReference,
    state,
  } = props;

  const data = usePreloadedQuery(load_registers, queryReference);

  const {
    data: registersData,
    hasNext,
    isLoadingNext,
    loadNext,
    refetch: refresh,
  } = usePaginationFragment<LoadRegistersPaginationQuery, LoadRegisters_query_profiles$key>(
    load_registers_pagination,
    data
  );

  const [loadMutationProgressQueryReference, loadMutationProgressQuery] =
    useQueryLoader<LoadMutationProgressQuery>(load_mutation_progress);

  const { user } = useAuth();

  useEffect(() => {
    if (user?.profile?.id) {
      loadMutationProgressQuery(
        {
          block: state.values.block,
          profileId: user.profile.id,
          skipInvoiceTotals: true,
          startingYear: state.values.startingYear,
        },
        { fetchPolicy: "network-only" }
      );
    }
  }, [loadMutationProgressQuery, user?.profile?.id, state.values.startingYear, state.values.block]);

  useEffect(() => {
    if (registersData.profiles?.edges && registersData.profiles.edges.length > 0) {
      const mutableRegistersData = [...registersData.profiles.edges];
      dispatchState({
        input: "dataProvider",
        value: state.values.dataProvider.cloneWithRows(
          // remove teachers with no lessons
          mutableRegistersData.filter((item) => (item?.node?.totalLessons ?? 0) > 0)
        ),
      });
      // if there is no data, create a fresh DataProvider
    } else {
      dispatchState({
        input: "dataProvider",
        value: new DataProvider((r1, r2) => {
          return r1 !== r2;
        }),
      });
    }

    dispatchState({
      input: "refreshHandler",
      value: () => {
        dispatchState({ input: "contentIsRendered", value: false });
        refresh(
          {
            first: LOAD_X_SCHOOLS,
            searchTerm: state.values.searchTerm.trim(),
          },
          {
            fetchPolicy: "network-only",
          }
        );
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registersData]);

  const onEndReached = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(LOAD_X_SCHOOLS);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  // stop refetch useEffects firing on first render
  const renderCount = useRef(0);
  const environment = useRelayEnvironment();
  const { colors } = useTheme();

  const refetchRegisters = useCallback(
    (variables: {
      block: number;
      first: number;
      searchTerm?: string;
      startingYear: number;
    }): Subscription | undefined => {
      dispatchState({ input: "isRefetching", value: true });

      const subscription = fetchQuery<LoadRegistersQuery>(environment, load_registers, variables, {
        fetchPolicy: "store-or-network",
      }).subscribe({
        complete: () => {
          dispatchState({ input: "isRefetching", value: false });
          refresh(variables, { fetchPolicy: "store-only" });
        },
        error: () => {
          dispatchState({ input: "isRefetching", value: false });
        },
      });
      return subscription;
    },
    [dispatchState, environment, refresh]
  );

  useEffect(() => {
    if (renderCount.current < 2) {
      renderCount.current += 1;
      return;
    }

    dispatchState({ input: "isRefetching", value: false });
    state.values.subscription?.unsubscribe();

    // fire refetch immediately when orderBy or filters change
    dispatchState({
      input: "subscription",
      value: refetchRegisters({
        block: state.values.block,
        first: LOAD_X_SCHOOLS,
        searchTerm: state.values.searchTerm.trim(),
        startingYear: state.values.startingYear,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.values.startingYear, state.values.block]);

  useDebounceFunction(
    () => {
      if (renderCount.current < 2) {
        renderCount.current += 1;
        return;
      }

      dispatchState({ input: "isRefetching", value: false });
      state.values.subscription?.unsubscribe();

      // fire refetch immediately when orderBy or filters change
      dispatchState({
        input: "subscription",
        value: refetchRegisters({
          block: state.values.block,
          first: LOAD_X_SCHOOLS,
          searchTerm: state.values.searchTerm.trim(),
          startingYear: state.values.startingYear,
        }),
      });
    }, // no delay if clearing text
    state.values.searchTerm !== "" ? 750 : 0,

    [state.values.searchTerm]
  );

  const { scale, width: windowWidth } = useWindowDimensions();

  useEffect(() => {
    // this allows the RLV to resize when the window resizes
    dispatchState({
      input: "layoutProvider",
      value: new LayoutProvider(
        () => "VSEL",
        (type, dim) => {
          switch (type) {
            case "VSEL":
              dim.width = getScaledWindowDimension(windowWidth, scale);
              dim.height = 40;
              break;
            default:
              dim.width = 0;
              dim.height = 0;
          }
        }
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth, scale]);

  const onPressProfile = useCallback(
    (profileId?: string | null) => {
      if (profileId) {
        navigation.navigate("Account", {
          accountType: "TEACHER",
          block: state.values.block,
          profileId: profileId,
          startingYear: state.values.startingYear,
        });
      }
    },
    [navigation, state.values.block, state.values.startingYear]
  );

  const rowRenderer = useCallback(
    (_: unknown, data: TableData, index: number): ReactElement | null => {
      if (data?.node) {
        return (
          <Box borderColor="surface.400" borderLeftWidth={TABLE_BORDER_WIDTH}>
            <RegisterHubTableRow
              cellProps={{
                hideTopBorder: index === 0,
                pressableTextProps: {
                  fontFamily: "Poppins-Regular",
                },
                px: 2,
                textProps: {
                  fontFamily: "Poppins-Light",
                  fontSize: "md",
                  textAlign: "center",
                },
              }}
              data={data.node}
              flexArray={FLEX_ARRAY}
              onPressProfile={onPressProfile}
              rowHeight={10}
              tableBorderColor={TABLE_BORDER_COLOR}
              tableBorderWidth={TABLE_BORDER_WIDTH}
            />
          </Box>
        );
      } else {
        return null;
      }
    },
    [onPressProfile]
  );

  const renderFooter = useCallback(() => {
    return isLoadingNext ? (
      <CustomFlatListSpinner
        borderColor={TABLE_BORDER_COLOR}
        borderTopWidth={1}
        position="relative"
        pt="8"
        top="0"
      />
    ) : null;
  }, [isLoadingNext]);

  useEffect(() => {
    dispatchState({ input: "contentIsRendered", value: true });
    // unsubscribe from refetch on unmount
    return () => state.values.subscription?.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryReference]);

  if (!state.values.contentIsRendered) {
    return <LoadingBlobs>Loading Registers...</LoadingBlobs>;
  }

  return (
    <>
      <Box flex={1} mx="30px">
        {state.values.dataProvider.getSize() > 0 && state.values.layoutProvider ? (
          <RecyclerListView
            canChangeSize
            dataProvider={state.values.dataProvider}
            layoutProvider={state.values.layoutProvider}
            onEndReached={onEndReached}
            onEndReachedThreshold={2000}
            renderAheadOffset={800}
            renderFooter={renderFooter}
            rowRenderer={rowRenderer}
            scrollViewProps={{
              contentContainerStyle: {
                borderBottomRadius: TABLE_BORDER_RADIUS,
                borderBottomWidth:
                  (registersData.profiles?.edges.length ?? 0 > 0) && !isLoadingNext
                    ? TABLE_BORDER_WIDTH
                    : 0,
                borderColor:
                  // @ts-expect-error can't index with variable
                  colors?.[TABLE_BORDER_COLOR.split(".")[0]][TABLE_BORDER_COLOR.split(".")[1]],
                marginBottom: 40,
                overflow: "hidden",
              },
              showsVerticalScrollIndicator: false,
            }}
            style={{
              flex: 1,
              minHeight: 1,
            }}
            suppressBoundedSizeException
            useWindowScroll
          />
        ) : (
          <ListEmptyBanner
            actionButton={
              <Button
                _hover={{ bg: "primary.500" }}
                _pressed={{ bg: "primary.600" }}
                _text={{ fontSize: "17" }}
                bg="primary.400"
                isLoading={
                  generateRegistersForNewBlockInFlight ||
                  Boolean(
                    user?.profile?.profileGroup?.generateRegistersForNewBlockProgress != null &&
                      user?.profile?.profileGroup?.generateRegistersForNewBlockProgress >= 0 &&
                      user?.profile?.profileGroup?.generateRegistersForNewBlockProgress < 100
                  )
                }
                isLoadingText={"Generating Registers"}
                leftIcon={<RegisterIcon size="md" />}
                onPress={() => {
                  dispatchState({
                    input: "generateNewRegistersForTeachersTeacherSelectModalOpen",
                    value: true,
                  });
                }}
                px="4"
                shadow={1}
              >
                Copy Registers from Previous Block
              </Button>
            }
            explainer={"No registers found..."}
          >
            <Text fontSize="6xl">{"😐"}</Text>
          </ListEmptyBanner>
        )}
      </Box>
      {loadMutationProgressQueryReference ? (
        <RegisterMutationProgressIndicators
          ref={progressIndicatorRef}
          dispatchState={dispatchState}
          loadMutationProgressQuery={loadMutationProgressQuery}
          loadMutationProgressQueryReference={loadMutationProgressQueryReference}
          state={state}
        />
      ) : null}
    </>
  );
};

const RegistersHubScreen: FC<ScreenProps> = (props) => {
  const { navigation, route } = props;

  const { user } = useAuth();

  const [defaultStartingYear, startingYears] = useMemo(
    () => getDefaultStartingYearAndStartingYearsForSelect(),
    []
  );

  const initialState = useMemo(() => {
    let initialStartingYear = route.params?.startingYear;
    if (initialStartingYear === undefined) {
      initialStartingYear = user?.profile?.profileGroup?.currentStartingYear ?? defaultStartingYear;
      navigation.setParams({
        startingYear: initialStartingYear,
      });
    }
    let initialBlock = route.params?.block;
    if (initialBlock === undefined) {
      if (
        user?.profile?.profileGroup?.currentStartingYear &&
        user?.profile?.profileGroup?.currentStartingYear > defaultStartingYear
      ) {
        initialBlock = 1;
      } else {
        initialBlock = user?.profile?.currentBlock ?? getCurrentBlock();
      }
      navigation.setParams({
        block: initialBlock,
      });
    }

    return {
      values: {
        block: Number(initialBlock),
        contentIsRendered: false,
        dataProvider: new DataProvider((r1, r2) => {
          return r1 !== r2;
        }),
        failedMutationItems: [],
        generateNewRegistersAlertIsOpen: false,
        generateNewRegistersForTeachersAlertIsOpen: false,
        generateNewRegistersForTeachersTeacherSelectModalOpen: false,
        generateSchoolGoogleRegistersModalIsOpen: false,
        generateTeacherGoogleRegistersModalIsOpen: false,
        isRefetching: false,
        layoutProvider: undefined,
        readInRegistersModalIsOpen: false,
        refreshHandler: undefined,
        searchTerm: route.params?.searchTerm ?? "",
        showMutationProgressIndicators: {
          generateGoogleRegisters: false,
          generateRegistersForNewBlock: false,
          readInRegisters: false,
          sendTeacherBulkCommunication: false,
        },
        startingYear: Number(initialStartingYear),
        subscription: undefined,
        teacherIds: [],
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reducer = createReducer<ReducerValues>(initialState);
  const [state, dispatchState] = useReducer(reducer, initialState);

  const [loadRegistersQueryReference, loadRegistersQuery] =
    useQueryLoader<LoadRegistersQuery>(load_registers);

  useEffect(() => {
    if (!state.values.contentIsRendered) {
      loadRegistersQuery(
        {
          block: state.values.block,
          first: LOAD_X_SCHOOLS,
          searchTerm: state.values.searchTerm.trim(),
          startingYear: state.values.startingYear,
        },
        { fetchPolicy: "store-or-network" }
      );
    }
  }, [
    loadRegistersQuery,
    state.values.block,
    state.values.contentIsRendered,
    state.values.searchTerm,
    state.values.startingYear,
  ]);

  const toast = useToast();

  const progressIndicatorRef = useRef<RegisterMutationProgressIndicatorsRef>(null);

  const sendRegisterChangeNotificationModalKey = useRef(0);

  const [commitGenerateOrUpdateGoogleRegisters, generateOrUpdateGoogleRegistersInFlight] =
    useMutation<GenerateOrUpdateGoogleRegistersMutation>(generate_or_update_google_registers);

  const generateOrUpdateGoogleRegisters = useCallback(
    (variables: {
      inputIds: string[];
      isSchoolRegisters: boolean;
      readInRegisterMarkings?: boolean;
    }) => {
      let hasErrored = false;
      const failedMutationItems: string[] = [];

      const generateOrUpdateGoogleRegistersConfig = (
        numberOfRegistersGenerated: number | undefined,
        inputIds: string[]
      ) => {
        progressIndicatorRef.current?.refreshMutationProgress();
        return {
          variables: {
            input: {
              block: state.values.block,
              inputIds: inputIds,
              isSchoolRegisters: variables.isSchoolRegisters,
              numberOfRegistersGenerated: numberOfRegistersGenerated,
              readInRegisterMarkings: variables.readInRegisterMarkings,
              registersInBatch: REGISTERS_TO_GENERATE_IN_BATCH,
              startingYear: state.values.startingYear,
            },
          },
          onCompleted: (response: GenerateOrUpdateGoogleRegistersMutation$data) => {
            let label = "";
            if (response.generateOrUpdateGoogleRegisters?.erroredTeacher) {
              if (response.generateOrUpdateGoogleRegisters?.erroredTeacher?.user.firstName) {
                label = getFullName(
                  response.generateOrUpdateGoogleRegisters.erroredTeacher.user.firstName,
                  response.generateOrUpdateGoogleRegisters.erroredTeacher.user.lastName
                );
              } else {
                label = response.generateOrUpdateGoogleRegisters.erroredTeacher?.user.email ?? "";
              }
            } else if (response.generateOrUpdateGoogleRegisters?.erroredSchool) {
              label =
                response.generateOrUpdateGoogleRegisters?.erroredSchool?.name ?? "un-named school";
            }
            if (label != "") {
              failedMutationItems.push(label);
              toast.show({
                // never auto-dismiss
                duration: null,
                render: ({ id }: { id: string }) => (
                  <ToastAlert
                    description={failedMutationItems.join("\n")}
                    id={id}
                    status="error"
                    title={"Couldn't generate registers for:"}
                    toast={toast}
                  />
                ),
              });
            }
            if (response?.generateOrUpdateGoogleRegisters?.success) {
              if (
                response.generateOrUpdateGoogleRegisters.numberOfRegistersGenerated !==
                  response.generateOrUpdateGoogleRegisters.totalToGenerate &&
                !hasErrored
              ) {
                if (
                  response.generateOrUpdateGoogleRegisters.profileGroup
                    ?.generateGoogleRegistersProgress != 100
                ) {
                  commitGenerateOrUpdateGoogleRegisters(
                    generateOrUpdateGoogleRegistersConfig(
                      response.generateOrUpdateGoogleRegisters.numberOfRegistersGenerated ??
                        undefined,
                      variables.inputIds
                    )
                  );
                }
              }
            } else {
              hasErrored = true;
              toast.show({
                // never auto-dismiss
                duration: null,
                render: ({ id }: { id: string }) => (
                  <ToastAlert
                    description={"Please get in touch with one of the team"}
                    id={id}
                    status="error"
                    title={"Error generating registers"}
                    toast={toast}
                  />
                ),
              });
            }
          },
          // this happens if the API gateway times out for a register
          // this will happen for nearly every register, but is not a problem
          // we just need to wait long enough for the register to finish generating
          // before firing off another mutation and continuing
          onError: () => {
            progressIndicatorRef.current?.refreshMutationProgress();
            if (
              numberOfRegistersGenerated !== undefined &&
              numberOfRegistersGenerated + 1 < variables.inputIds.length
            ) {
              setTimeout(() => {
                commitGenerateOrUpdateGoogleRegisters(
                  generateOrUpdateGoogleRegistersConfig(
                    numberOfRegistersGenerated + 1,
                    variables.inputIds
                  )
                );
                // give each register 4 minutes to generate
              }, 180000);
            }
          },
        };
      };

      // to avoid hitting the max AWS API Gateway execution time (29s)
      // split up the invoice generation into batches and recursively
      // fire the mutation, keeping a track of overall progress
      const numberOfRegistersGenerated: number | undefined = 0;

      commitGenerateOrUpdateGoogleRegisters(
        generateOrUpdateGoogleRegistersConfig(numberOfRegistersGenerated, variables.inputIds)
      );
      dispatchState({
        input: "showMutationProgressIndicators",
        value: {
          ...state.values.showMutationProgressIndicators,
          generateGoogleRegisters: true,
        },
      });
    },
    [state.values, commitGenerateOrUpdateGoogleRegisters, toast]
  );

  const [commitReadInGoogleRegisters, readInGoogleRegistersInFlight] =
    useMutation<ReadInGoogleRegistersMutation>(read_in_google_registers);

  const readInGoogleRegisters = useCallback(
    (variables: { teacherIds: string[] }) => {
      let hasErrored = false;
      const failedMutationItems: string[] = [];

      const readInGoogleRegistersConfig = (
        numberOfRegistersReadIn: number | undefined,
        teacherIds: string[]
      ) => {
        return {
          variables: {
            input: {
              block: state.values.block,
              numberOfRegistersReadIn: numberOfRegistersReadIn,
              registersInBatch: REGISTERS_TO_READ_IN_BATCH,
              startingYear: state.values.startingYear,
              teacherIds: teacherIds,
            },
          },
          onCompleted: (response: ReadInGoogleRegistersMutation$data) => {
            if (response.readInGoogleRegisters?.erroredTeacher) {
              let label = "";
              if (response.readInGoogleRegisters?.erroredTeacher?.user.firstName) {
                label = getFullName(
                  response.readInGoogleRegisters.erroredTeacher.user.firstName,
                  response.readInGoogleRegisters.erroredTeacher.user.lastName
                );
              } else {
                label = response.readInGoogleRegisters.erroredTeacher?.user.email;
              }

              failedMutationItems.push(label);

              toast.show({
                // never auto-dismiss
                duration: null,
                render: ({ id }: { id: string }) => (
                  <ToastAlert
                    description={failedMutationItems.join("\n")}
                    id={id}
                    status="error"
                    title={"Couldn't read in registers for:"}
                    toast={toast}
                  />
                ),
              });
            }
            if (response?.readInGoogleRegisters?.success) {
              if (
                response.readInGoogleRegisters.numberOfRegistersReadIn !==
                  response.readInGoogleRegisters.totalToReadIn &&
                !hasErrored
              ) {
                if (response.readInGoogleRegisters.profileGroup?.readInRegistersProgress != 100) {
                  commitReadInGoogleRegisters(
                    readInGoogleRegistersConfig(
                      response.readInGoogleRegisters.numberOfRegistersReadIn ?? undefined,
                      variables.teacherIds
                    )
                  );
                }
              }
            } else {
              hasErrored = true;
              toast.show({
                // never auto-dismiss
                duration: null,
                render: ({ id }: { id: string }) => (
                  <ToastAlert
                    description={"Please get in touch with one of the team"}
                    id={id}
                    status="error"
                    title={"Error reading in registers"}
                    toast={toast}
                  />
                ),
              });
            }
          },
          // this happens if the API gateway times out for a register
          // in this case, just fire off another mutation and continue
          onError: () => {
            progressIndicatorRef.current?.refreshMutationProgress();
            // wait for 30 seconds to let the previous register complete (if it is completing)
            setTimeout(() => {
              if (numberOfRegistersReadIn !== undefined) {
                commitReadInGoogleRegisters(
                  readInGoogleRegistersConfig(numberOfRegistersReadIn + 1, variables.teacherIds)
                );
              }
            }, 30000);
          },
        };
      };

      // to avoid hitting the max AWS API Gateway execution time (29s)
      // split up the invoice generation into batches and recursively
      // fire the mutation, keeping a track of overall progress
      const numberOfRegistersReadIn: number | undefined = 0;

      commitReadInGoogleRegisters(
        readInGoogleRegistersConfig(numberOfRegistersReadIn, variables.teacherIds)
      );
      dispatchState({
        input: "showMutationProgressIndicators",
        value: {
          ...state.values.showMutationProgressIndicators,
          readInRegisters: true,
        },
      });
    },
    [state.values, commitReadInGoogleRegisters, toast]
  );

  const [commitGenerateRegistersForNewBlock, generateRegistersForNewBlockInFlight] =
    useMutation<GenerateRegistersForNewBlockMutation>(generate_registers_for_new_block);

  const generateRegistersForNewBlock = useCallback(
    (teacherIds?: string[]) => {
      const generateRegistersForNewBlockConfig = {
        variables: {
          input: {
            block: state.values.block,
            startingYear: state.values.startingYear,
            teacherIds: teacherIds ?? [],
          },
        },
        onCompleted: (response: GenerateRegistersForNewBlockMutation$data) => {
          if (response?.generateRegistersForNewBlock?.success) {
            // refresh on complete
            setTimeout(() => {
              state.values.refreshHandler?.();
            }, 1000);
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="success"
                  title={"New registers generated"}
                  toast={toast}
                />
              ),
            });
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't generate new registers"}
                  toast={toast}
                />
              ),
            });
          }
        },
      };
      commitGenerateRegistersForNewBlock(generateRegistersForNewBlockConfig);
      dispatchState({
        input: "showMutationProgressIndicators",
        value: {
          ...state.values.showMutationProgressIndicators,
          generateRegistersForNewBlock: true,
        },
      });
    },
    [state.values, commitGenerateRegistersForNewBlock, toast]
  );

  const inputChangeHandler = useCallback(
    (_: unknown, inputValue?: string) => {
      dispatchState({
        input: "searchTerm",
        value: inputValue,
      });
      navigation.setParams({ searchTerm: inputValue });
    },
    [navigation]
  );

  const inputClearHandler = useCallback(() => {
    if (state.values.searchTerm !== "") {
      dispatchState({
        input: "searchTerm",
        value: "",
      });
      navigation.setParams({ searchTerm: undefined });
    }
  }, [navigation, state.values.searchTerm]);

  const {
    isOpen: registerActionsActionsheetIsOpen,
    onClose: registerActionsActionsheetOnClose,
    onOpen: registerActionsActionsheetOnOpen,
  } = useDisclose();

  const renderHeader = useMemo(() => {
    return (
      <VStack mx="30px" pt="6" space="6">
        <HStack ml="1" space="3">
          <RegisterIcon color="primary.600" size="2xl" />
          <Heading color="primary.600" fontSize="xl">
            Registers
          </Heading>
        </HStack>
        <HStack justifyContent="space-between">
          <HStack space="4">
            <Box minWidth="400" width="25%">
              <SearchBar
                inputClearHandler={inputClearHandler}
                inputOnChangeHandler={inputChangeHandler}
                inputSearchHandler={inputChangeHandler}
                placeholderText={"Search by teacher name or email"}
                searchText={state.values.searchTerm}
                showSearchIcon
              />
            </Box>
            <Select
              borderRadius="2xl"
              fontSize="md"
              onValueChange={(itemValue) => {
                const value = parseInt(itemValue);

                if (value > state.values.startingYear) {
                  dispatchState({
                    input: "block",
                    value: 1,
                  });
                  navigation.setParams({
                    block: 1,
                  });
                }

                dispatchState({
                  input: "startingYear",
                  value: value,
                });
                navigation.setParams({
                  startingYear: value,
                });
              }}
              placeholder="Select year"
              py="2.5"
              selectedValue={String(state.values.startingYear)}
              width="40"
            >
              {startingYears.map((item) => {
                return (
                  <Select.Item
                    key={item.value}
                    actionSheetLabel={item.label}
                    value={String(item.value)}
                  />
                );
              })}
            </Select>
            <Select
              borderRadius="2xl"
              fontSize="md"
              onValueChange={(itemValue) => {
                dispatchState({
                  input: "block",
                  value: parseInt(itemValue),
                });
                navigation.setParams({
                  block: parseInt(itemValue),
                });
              }}
              placeholder="Select block"
              py="2.5"
              selectedValue={String(state.values.block)}
              width="40"
            >
              {BLOCKS.map((item) => {
                return (
                  <Select.Item key={item.value} actionSheetLabel={item.label} value={item.value} />
                );
              })}
            </Select>
            <Button
              bg="surface.400"
              colorScheme="surface"
              leftIcon={<RestartIcon size="5" />}
              onPress={state.values.refreshHandler}
              p="3"
            />
          </HStack>
          <Box>
            <Button
              _hover={{ bg: "primary.500" }}
              _pressed={{ bg: "primary.600" }}
              _text={{ fontSize: "17" }}
              bg="primary.400"
              leftIcon={<RegisterIcon size="md" />}
              px="4"
              shadow={1}
              {...(generateOrUpdateGoogleRegistersInFlight ||
              Boolean(
                typeof user?.profile?.profileGroup?.generateGoogleRegistersProgress === "number" &&
                  user?.profile?.profileGroup?.generateGoogleRegistersProgress >= 0 &&
                  user?.profile?.profileGroup?.generateGoogleRegistersProgress < 100
              )
                ? {
                    isLoading: true,
                    isLoadingText: "Generating Registers",
                  }
                : readInGoogleRegistersInFlight ||
                    Boolean(
                      typeof user?.profile?.profileGroup?.readInRegistersProgress === "number" &&
                        user?.profile?.profileGroup?.readInRegistersProgress >= 0 &&
                        user?.profile?.profileGroup?.readInRegistersProgress < 100
                    )
                  ? {
                      isLoading: true,
                      isLoadingText: "Reading In Registers",
                    }
                  : generateRegistersForNewBlockInFlight ||
                      Boolean(
                        typeof user?.profile?.profileGroup?.generateRegistersForNewBlockProgress ===
                          "number" &&
                          user?.profile?.profileGroup?.generateRegistersForNewBlockProgress >= 0 &&
                          user?.profile?.profileGroup?.generateRegistersForNewBlockProgress < 100
                      )
                    ? {
                        isLoading: true,
                        isLoadingText: "Generating Registers",
                      }
                    : {})}
              onPress={() => registerActionsActionsheetOnOpen()}
            >
              View Register Actions
            </Button>
          </Box>
        </HStack>
        <Box bg="surface.100">
          <Box
            bg="primary.50"
            borderColor={TABLE_BORDER_COLOR}
            borderTopRadius={TABLE_BORDER_RADIUS}
            borderWidth={TABLE_BORDER_WIDTH}
          >
            <Row
              cellProps={{
                hideOuterSideBorder: true,
                hideTopBorder: true,
                px: 2,
                textProps: {
                  fontSize: "md",
                  textAlign: "center",
                },
              }}
              data={[
                {
                  data: "Teacher",
                },
                {
                  data: "Register Completion",
                },
              ]}
              flexArray={FLEX_ARRAY}
              rowHeight={10}
              rowIndex={0}
              tableBorderColor={TABLE_BORDER_COLOR}
              tableBorderWidth={TABLE_BORDER_WIDTH}
            />
          </Box>
        </Box>
      </VStack>
    );
  }, [
    generateOrUpdateGoogleRegistersInFlight,
    generateRegistersForNewBlockInFlight,
    inputChangeHandler,
    inputClearHandler,
    navigation,
    readInGoogleRegistersInFlight,
    registerActionsActionsheetOnOpen,
    startingYears,
    state.values.block,
    state.values.refreshHandler,
    state.values.searchTerm,
    state.values.startingYear,
    user?.profile?.profileGroup?.generateGoogleRegistersProgress,
    user?.profile?.profileGroup?.generateRegistersForNewBlockProgress,
    user?.profile?.profileGroup?.readInRegistersProgress,
  ]);

  const safeGenerateRegistersAlertRef = useRef(null);
  const schoolModalKey = useRef(0);

  const renderRegisterActionsActionsheet = useMemo(() => {
    const showRegisterChangeNotificationLoadingSpinner =
      state.values.sendTeacherBulkCommunicationInProgress ||
      Boolean(
        typeof user?.profile?.profileGroup?.sendTeacherBulkCommunicationProgress === "number" &&
          user?.profile?.profileGroup?.sendTeacherBulkCommunicationProgress >= 0 &&
          user?.profile?.profileGroup?.sendTeacherBulkCommunicationProgress < 100
      );

    return (
      <Actionsheet
        animationType="fade"
        hideDragIndicator
        isOpen={registerActionsActionsheetIsOpen}
        justifyContent="center"
        onClose={registerActionsActionsheetOnClose}
        size="lg"
      >
        <Actionsheet.Content
          alignItems="center"
          closeButtonProps={{ right: "5", top: "5" }}
          justifyContent="center"
          mx="auto"
          roundedBottom={12}
          roundedTop={12}
          showCloseButton
          w="40%"
        >
          <Text alignSelf="center" fontSize="xl" fontWeight="bold" pb="2" pt="4">
            Register Actions
          </Text>
          <Text alignSelf="center" fontSize="md" pb="5">
            {getTermInWords(state.values.block, state.values.startingYear)}
          </Text>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="7">
                <AccountsIcon color="primary.600" size="5" />
              </Center>
            }
            onPress={() => {
              registerActionsActionsheetOnClose();
              dispatchState({
                input: "generateTeacherGoogleRegistersModalIsOpen",
                value: true,
              });
            }}
          >
            Generate or update teacher registers
          </Actionsheet.Item>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="7">
                <SchoolIcon color="primary.500" size="5" />
              </Center>
            }
            onPress={() => {
              registerActionsActionsheetOnClose();
              dispatchState({
                input: "generateSchoolGoogleRegistersModalIsOpen",
                value: true,
              });
            }}
          >
            Generate or update school registers
          </Actionsheet.Item>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            isDisabled={showRegisterChangeNotificationLoadingSpinner}
            leftIcon={
              <Center size="7">
                {showRegisterChangeNotificationLoadingSpinner ? (
                  <Spinner />
                ) : (
                  <SendIcon color="secondary.500" size="5" />
                )}
              </Center>
            }
            onPress={() => {
              registerActionsActionsheetOnClose();
              dispatchState({
                input: "sendRegisterChangeNotificationModalIsOpen",
                value: true,
              });
            }}
          >
            {"Send register update notifications"}
          </Actionsheet.Item>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="7">
                <SortDescDate color="secondary.600" size="6" />
              </Center>
            }
            onPress={() => {
              registerActionsActionsheetOnClose();
              dispatchState({
                input: "readInRegistersModalIsOpen",
                value: true,
              });
            }}
          >
            {"Read in register data"}
          </Actionsheet.Item>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="7">
                <CopyCalendarIcon color="red.500" size="6" />
              </Center>
            }
            onPress={() => {
              registerActionsActionsheetOnClose();
              dispatchState({
                input: "generateNewRegistersAlertIsOpen",
                value: true,
              });
            }}
          >
            Copy all registers from previous block
          </Actionsheet.Item>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="7">
                <CopyCalendarIcon color="red.500" size="6" />
              </Center>
            }
            onPress={() => {
              registerActionsActionsheetOnClose();
              dispatchState({
                input: "generateNewRegistersForTeachersTeacherSelectModalOpen",
                value: true,
              });
            }}
          >
            Copy registers from previous block by teacher
          </Actionsheet.Item>
          <Text
            alignSelf="center"
            fontFamily="Poppins-Regular"
            fontSize="md"
            mx="8"
            py="4"
            textAlign="center"
          >
            {
              "After clicking on an action above, you have the option to select which teachers/schools to do it for."
            }
          </Text>
        </Actionsheet.Content>
      </Actionsheet>
    );
  }, [
    registerActionsActionsheetIsOpen,
    registerActionsActionsheetOnClose,
    state.values.block,
    state.values.sendTeacherBulkCommunicationInProgress,
    state.values.startingYear,
    user?.profile?.profileGroup?.sendTeacherBulkCommunicationProgress,
  ]);

  const renderRegisterGenerateTeacherSelectModal = useMemo(() => {
    return (
      <TeacherMultiSelectModal
        buttonText="Generate"
        checkboxDefaultIsSelected={true}
        checkboxText="Read in register markings"
        description={getTermInWords(state.values.block, state.values.startingYear)}
        hideModal={() => {
          dispatchState({
            input: "generateTeacherGoogleRegistersModalIsOpen",
            value: false,
          });
        }}
        includeCheckbox={true}
        initiallySelectAllTeachers
        onSave={({ checkboxOptionIsTrue: readInRegisterMarkings, teacherIds }) =>
          generateOrUpdateGoogleRegisters({
            inputIds: teacherIds,
            isSchoolRegisters: false,
            readInRegisterMarkings: readInRegisterMarkings,
          })
        }
        showModal={state.values.generateTeacherGoogleRegistersModalIsOpen}
        title="Generate Or Update Registers"
      />
    );
  }, [
    generateOrUpdateGoogleRegisters,
    state.values.block,
    state.values.generateTeacherGoogleRegistersModalIsOpen,
    state.values.startingYear,
  ]);

  const renderRegisterReadInTeacherSelectModal = useMemo(() => {
    return (
      <TeacherMultiSelectModal
        buttonText="Read In"
        description={getTermInWords(state.values.block, state.values.startingYear)}
        hideModal={() => {
          dispatchState({
            input: "readInRegistersModalIsOpen",
            value: false,
          });
        }}
        initiallySelectAllTeachers
        onSave={({ teacherIds }) =>
          readInGoogleRegisters({
            teacherIds,
          })
        }
        showModal={state.values.readInRegistersModalIsOpen}
        title="Read In Registers"
      />
    );
  }, [
    readInGoogleRegisters,
    state.values.block,
    state.values.readInRegistersModalIsOpen,
    state.values.startingYear,
  ]);

  const renderSchoolSelectModal = useMemo(() => {
    return (
      <SchoolMultiSelectModal
        // reset modal state each time it's opened
        key={schoolModalKey.current}
        buttonText="Generate"
        description={getTermInWords(state.values.block, state.values.startingYear)}
        hideModal={() => {
          dispatchState({
            input: "generateSchoolGoogleRegistersModalIsOpen",
            value: false,
          });
          setTimeout(() => {
            schoolModalKey.current += 1;
          }, 500);
        }}
        initiallySelectAllSchools
        onSave={({ schoolIds }) =>
          generateOrUpdateGoogleRegisters({
            inputIds: schoolIds,
            isSchoolRegisters: true,
          })
        }
        showModal={state.values.generateSchoolGoogleRegistersModalIsOpen}
        title="Generate Or Update Registers"
      />
    );
  }, [
    generateOrUpdateGoogleRegisters,
    state.values.block,
    state.values.generateSchoolGoogleRegistersModalIsOpen,
    state.values.startingYear,
  ]);

  const renderSendRegisterChangeNotificationModal = useMemo(() => {
    return (
      <SendRegisterChangeNotificationModal
        key={sendRegisterChangeNotificationModalKey.current}
        hideModal={() => {
          dispatchState({
            input: "sendRegisterChangeNotificationModalIsOpen",
            value: false,
          });
          // reset state on close
          setTimeout(() => {
            sendRegisterChangeNotificationModalKey.current = Math.random();
          }, 500);
        }}
        onSendFinish={(success) => {
          dispatchState({
            input: "sendTeacherBulkCommunicationInProgress",
            value: false,
          });
          if (success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert id={id} status="success" title={"Messages Sent"} toast={toast} />
              ),
            });
          }
        }}
        onSendStart={({ teacherIds }) => {
          // don't show the progress indicator if only sending to one teacher
          if ((teacherIds?.length ?? 0) > 1) {
            dispatchState({
              input: "showMutationProgressIndicators",
              value: {
                ...state.values.showMutationProgressIndicators,
                sendTeacherBulkCommunication: true,
              },
            });
            dispatchState({
              input: "sendTeacherBulkCommunicationInProgress",
              value: true,
            });
          }
        }}
        showModal={Boolean(state.values.sendRegisterChangeNotificationModalIsOpen)}
      />
    );
  }, [
    state.values.sendRegisterChangeNotificationModalIsOpen,
    state.values.showMutationProgressIndicators,
    toast,
  ]);

  const renderModals = useMemo(() => {
    return (
      <>
        <AlertPopup
          alertIsOpen={state.values.generateNewRegistersAlertIsOpen}
          body={`This action will copy the registers from the preceeding block to this block.\n\nThe lesson dates will obey the term dates and blacklisted dates set for this block. Ensure these are correct before proceeding.${
            state.values.block == 1
              ? "\n\nAs this is the first block of a new school year, this action will also move the system to the new school year. This means that children in their final year of school will be archived and not put on the register for the new term, and the year group of all other children will be increased."
              : ""
          }`}
          bodyStyle={{ _text: { fontSize: "md" } }}
          header={`Are you sure you want to generate new registers for this block${
            state.values.block == 1 ? " and move to the next year?" : "?"
          }`}
          safeButtonRef={safeGenerateRegistersAlertRef}
          setAlertIsOpen={() => {
            dispatchState({
              input: "generateNewRegistersAlertIsOpen",
              value: false,
            });
          }}
        >
          <Button
            ref={safeGenerateRegistersAlertRef}
            _text={{ fontSize: "lg" }}
            colorScheme="surface"
            height="80px"
            minWidth="56"
            onPress={() => {
              dispatchState({
                input: "generateNewRegistersAlertIsOpen",
                value: false,
              });
            }}
            width="25%"
          >
            {"No, don't generate\nthe registers"}
          </Button>
          <ButtonDebounced
            _loading={{ opacity: 1 }}
            _spinner={{ size: "lg" }}
            _text={{ fontSize: "lg" }}
            height="80px"
            minWidth="56"
            onPress={() => {
              dispatchState({
                input: "generateNewRegistersAlertIsOpen",
                value: false,
              });
              generateRegistersForNewBlock();
            }}
            width="25%"
          >
            {"Yes, generate\nnew registers"}
          </ButtonDebounced>
        </AlertPopup>
        <TeacherMultiSelectModal
          block={
            getPreviousBlockAndStartingYear(state.values.block, state.values.startingYear)[0] ?? 0
          }
          buttonText="Copy"
          hideModal={() => {
            dispatchState({
              input: "generateNewRegistersForTeachersTeacherSelectModalOpen",
              value: false,
            });
          }}
          includeCheckbox={false}
          onSave={({ teacherIds }) => {
            dispatchState({
              input: "generateNewRegistersForTeachersAlertIsOpen",
              value: true,
            });
            dispatchState({
              input: "teacherIds",
              value: teacherIds,
            });
          }}
          showModal={state.values.generateNewRegistersForTeachersTeacherSelectModalOpen}
          showRegisterProgress
          startingYear={
            getPreviousBlockAndStartingYear(state.values.block, state.values.startingYear)[1] ?? 0
          }
          title={`Copy registers from ${getTermInWords(
            getPreviousBlockAndStartingYear(state.values.block, state.values.startingYear)[0],
            getPreviousBlockAndStartingYear(state.values.block, state.values.startingYear)[1]
          )}`}
        />
        <AlertPopup
          alertIsOpen={state.values.generateNewRegistersForTeachersAlertIsOpen}
          body={`This action will copy the registers from the preceeding block to this block for ${
            state.values.teacherIds?.length ?? 0
          } teacher${
            state.values.teacherIds?.length === 1 ? "" : "s"
          }.\n\nThe lesson dates will obey the term dates and blacklisted dates set for this block. Ensure these are correct before proceeding.${
            state.values.block == 1 && !state.values.teacherIds?.length
              ? "\n\nAs this is the first block of a new school year, children in their final year of school will be archived and not put on the register for the new term, and the year group of all other children will be increased."
              : ""
          }`}
          bodyStyle={{ _text: { fontSize: "md" } }}
          header={`Are you sure you want to generate new registers for ${
            state.values.teacherIds?.length ?? 0
          } teacher${state.values.teacherIds?.length === 1 ? "" : "s"} for this block${
            state.values.block == 1 && !state.values.teacherIds?.length
              ? " and move to the next year?"
              : "?"
          }`}
          safeButtonRef={safeGenerateRegistersAlertRef}
          setAlertIsOpen={() => {
            dispatchState({
              input: "generateNewRegistersForTeachersAlertIsOpen",
              value: false,
            });
          }}
        >
          <Button
            ref={safeGenerateRegistersAlertRef}
            _text={{ fontSize: "lg" }}
            colorScheme="surface"
            height="80px"
            minWidth="56"
            onPress={() => {
              dispatchState({
                input: "generateNewRegistersForTeachersAlertIsOpen",
                value: false,
              });
            }}
            width="25%"
          >
            {"No, don't generate\nthe registers"}
          </Button>
          <ButtonDebounced
            _loading={{ opacity: 1 }}
            _spinner={{ size: "lg" }}
            _text={{ fontSize: "lg" }}
            height="80px"
            minWidth="56"
            onPress={() => {
              dispatchState({
                input: "generateNewRegistersForTeachersAlertIsOpen",
                value: false,
              });
              generateRegistersForNewBlock(state.values.teacherIds);
            }}
            width="25%"
          >
            {"Yes, generate\nnew registers"}
          </ButtonDebounced>
        </AlertPopup>
      </>
    );
  }, [
    state.values.block,
    state.values.startingYear,
    state.values.generateNewRegistersAlertIsOpen,
    state.values.generateNewRegistersForTeachersTeacherSelectModalOpen,
    state.values.generateNewRegistersForTeachersAlertIsOpen,
    state.values.teacherIds,
    generateRegistersForNewBlock,
  ]);

  const renderRefetchIndicator = useMemo(() => {
    return state.values.isRefetching ? <CustomFlatListSpinner top="275" /> : null;
  }, [state.values.isRefetching]);

  return (
    <Box bg="surface.100" flex={1} pt="70">
      {renderHeader}
      {loadRegistersQueryReference ? (
        <Suspense fallback={<LoadingBlobs>Loading Registers...</LoadingBlobs>}>
          <RegistersHubContent
            dispatchState={dispatchState}
            generateRegistersForNewBlockInFlight={generateRegistersForNewBlockInFlight}
            navigation={navigation}
            progressIndicatorRef={progressIndicatorRef}
            queryReference={loadRegistersQueryReference}
            state={state}
          />
        </Suspense>
      ) : (
        <LoadingBlobs>Loading Registers...</LoadingBlobs>
      )}
      {renderRegisterActionsActionsheet}
      {renderRegisterGenerateTeacherSelectModal}
      {renderRegisterReadInTeacherSelectModal}
      {renderSchoolSelectModal}
      {renderSendRegisterChangeNotificationModal}
      {renderModals}
      {renderRefetchIndicator}
    </Box>
  );
};

export const screenOptions = {
  headerTitle: "Registers",
};

export default RegistersHubScreen;
